import { defineComponent as _defineComponent } from 'vue'

const MAX_FILE_UPLOAD_SIZE = 31457280
const __default__ = {}

import appCan from '@@/bits/app_can'
import { supportsRecording, supportsVideoRecording } from '@@/bits/av_recorder'
import type { ContentPickerSourceMenuListItem } from '@@/bits/content_picker'
import { ContentPickerSource } from '@@/bits/content_picker'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { FILE_TOO_LARGE_ERROR, UploadJob } from '@@/bits/uploader'
import { defineAsyncComponent } from '@@/bits/vue'
import { checkForPadletMiniChromeExtension } from '@@/bits/web_extensions'
import type { SearchFilters } from '@@/bits/web_search'
import type { AppUploadIntent } from '@@/native_bridge/actions'
import { browserFile, openCamera } from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import type { ContentPickerSearchMode } from '@@/pinia/content_picker'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { PadletSelectionIntent } from '@@/pinia/padlet_picker'
import type { DraftPost, User, Wall, WallPostProperties } from '@@/types'
import ContentPickerSourceMenu from '@@/vuecomponents/ContentPickerSourceMenu.vue'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'ContentPicker',
  props: {
    isOnline: { type: Boolean },
    contentSourceMenuList: null,
    preferredContentSourceMenuStartHeight: null,
    enableHiddenInputs: { type: Boolean },
    searchSuggestions: { default: undefined },
    searchModes: { default: undefined },
    defaultSearchMode: { default: undefined },
    appIntent: { default: undefined },
    canAcceptLinkInSearchBar: { type: Boolean, default: true },
    googleDriveUploadKey: { default: undefined },
    googleDrivePickerFileTypes: { default: 'all' },
    maxFileUploadSize: { default: MAX_FILE_UPLOAD_SIZE },
    shouldUploadFile: { type: Boolean, default: true },
    activeDraft: { default: undefined },
    xSearchBar: { type: Boolean, default: true },
    user: { default: undefined },
    wall: { default: undefined },
    wallPostProperties: { default: undefined },
    contentPickerContext: { default: undefined }
  },
  emits: ["close-content-picker", "upload-start", "upload-done", "upload-cancel", "upload-reject", "upload-error", "upload-progress", "select-file", "select-link", "set-caption", "show-extension-required", "update-active-draft"],
  setup(__props: any, { expose, emit }: { emit: ({
  (name: 'close-content-picker'): void
  (name: 'upload-start', payload: { file?: File; url?: string }): void
  (name: 'upload-done', url: string): void
  (name: 'upload-cancel', file: File): void
  (name: 'upload-reject', file: File): void
  (name: 'upload-error', file?: File): void
  (name: 'upload-progress', progress: number): void
  (
    name: 'select-file',
    payload: {
      file: File
      maxFileUploadSize?: number
    },
  ): void
  (name: 'select-link', link: string, payload?: { altText: string }): void
  (name: 'set-caption', caption: string): void
  (name: 'show-extension-required'): void
  (
    name: 'update-active-draft',
    wishContent: {
      wish_content: { attachment_props: any }
    },
  ): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { isOnline: boolean, contentSourceMenuList: Array<Array<ContentPickerSourceMenuListItem>>, preferredContentSourceMenuStartHeight: number, enableHiddenInputs: boolean, searchSuggestions: string[], searchModes: ContentPickerSearchMode[], defaultSearchMode: SearchFilters, appIntent: AppUploadIntent, canAcceptLinkInSearchBar: boolean, googleDriveUploadKey: string, googleDrivePickerFileTypes: 'all' | 'images' | 'images_videos', maxFileUploadSize: number, shouldUploadFile: boolean, activeDraft: DraftPost, xSearchBar: boolean, user: User, wall: Wall, wallPostProperties: WallPostProperties, contentPickerContext: string };

const PhotoBoothV2 = defineAsyncComponent(() => import('@@/vuecomponents/PhotoBoothV2.vue'))
const VideoRecorder = defineAsyncComponent(() => import('@@/vuecomponents/VideoRecorder.vue'))
const AudioRecorderV2 = defineAsyncComponent(() => import('@@/vuecomponents/AudioRecorderV2.vue'))
const PadletBrowser = defineAsyncComponent(() => import('@@/vuecomponents/padlet_browser.vue'))
const SurfacePadletPickerV2 = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePadletPickerV2.vue'))
const DrawingCanvas = defineAsyncComponent(() => import('@@/vuecomponents/DrawingCanvas.vue'))
const MapBrowser = defineAsyncComponent(() => import('@@/vuecomponents/map_browser.vue'))
const LocationPickerV2 = defineAsyncComponent(() => import('@@/vuecomponents/LocationPickerV2.vue'))
const ScreenshareRecorder = defineAsyncComponent(() => import('@@/vuecomponents/screenshare_recorder.vue'))
const SurfacePollBuilder = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePollBuilder.vue'))
const GoogleDrivePicker = defineAsyncComponent(() => import('@@/vuecomponents/GoogleDrivePicker.vue'))
const AiTalk = defineAsyncComponent(() => import('@@/vuecomponents/AiTalk.vue'))





const isChrome = device.chrome
const isFirefox = device.firefox
const isElectronApp = device.electronApp
const isMobile = device.mobile

const contentPickerStore = useContentPickerStore()
const { activeContentPickerPanel, xContentSourceMenu, isHiddenContentSourceMenu, contentPickerContext } =
  storeToRefs(contentPickerStore)
const { hideContentPickerPanel, hideContentSourceMenu, pickContentSource } = contentPickerStore

const isContentPickerContextMatch = computed(
  // Ignore contentPickerContext if props is not provided
  () => props.contentPickerContext == null || contentPickerContext.value === props.contentPickerContext,
)
const chromeExtensionInstalled = ref<boolean | undefined>(undefined)
const uploadJob = ref<UploadJob | null>(null)

const activePanel = computed<ContentPickerSource | null>(() => activeContentPickerPanel.value)

const screenshareEnabledByDefault = computed<boolean>(() => {
  const screenshareApiEnabled = (isChrome && !!window.chrome && !!window.chrome.desktopCapture) || isFirefox
  return supportsRecording && !isMobile && screenshareApiEnabled
})

const screenshareSupport = computed<boolean>(
  () => !device.zoomApp && supportsVideoRecording && !isMobile && (isChrome || isFirefox),
)

const canAppUseNativeCameraAndFilePicker = computed<boolean>(() => device.app && appCan('useNativeCameraAndFilePicker'))

const pollDraft = computed(() => {
  return props.activeDraft?.wish_content?.attachment_props?.type === 'poll'
    ? (props.activeDraft?.wish_content?.attachment_props as any)
    : null
})

const audioLengthLimit = computed<number | undefined>(() => {
  if (!isAppUsing('mediaRecorderQuotas')) return undefined
  return props.wall?.audio_length_limit
})

const videoLengthLimit = computed<number | undefined>(() => {
  if (!isAppUsing('mediaRecorderQuotas')) return undefined
  if (props.wallPostProperties?.video_length_limit) return props.wallPostProperties.video_length_limit // based on wall settings
  return props.wall?.video_length_limit // based on wall owner's plan
})

const uploadFile = (file: File): void => {
  uploadJob.value = new UploadJob(file, { maxFileSize: props.maxFileUploadSize })
  uploadJob.value.perform()

  uploadJob.value.on('done', (response) => onUploadDone(response.url as string))
  uploadJob.value.on('cancel', () => onUploadCancel(file))
  uploadJob.value.on('error', (response) => {
    if (response.error === FILE_TOO_LARGE_ERROR) {
      onUploadReject(file)
    } else {
      onUploadError(file)
    }
  })
}

const onUploadDone = (url: string): void => {
  uploadJob.value = null
  emit('upload-done', url)
  closePanel()
}

const onUploadCancel = (file: File): void => {
  emit('upload-cancel', file)
  closePanel()
}

const onUploadReject = (file: File): void => {
  emit('upload-reject', file)
  closePanel()
}

const onUploadError = (file: File): void => {
  emit('upload-error', file)
  closePanel()
}

const selectFile = (e): void => {
  if (!props.shouldUploadFile) {
    emit('select-file', { file: e.target.files[0] })
  } else {
    emit('upload-start', { file: e.target.files[0] })
    uploadFile(e.target.files[0])
  }
  closeSourceMenu()
}

const selectLink = (link: string, payload?: { altText: string }): void => {
  emit('select-link', link, payload)
  closePanel()
}

const selectWall = (wall: Wall): void => {
  selectLink(wall.links.permanent ?? wall.links.show)
}

const startFileUpload = (file, maxFileUploadSize): void => {
  if (!props.shouldUploadFile) {
    emit('select-file', { file, maxFileUploadSize })
  } else {
    emit('upload-start', { file })
    uploadFile(file)
  }
  closeSourceMenu()
}

const setCaption = (caption: string): void => {
  if (!caption) {
    return
  }

  emit('set-caption', caption)
}

const closeSourceMenu = (): void => {
  hideContentSourceMenu()
  // If we close source menu without selecting a source, close the whole content picker.
  if (activeContentPickerPanel.value == null) {
    emit('close-content-picker')
  }
}

const closePanel = (): void => {
  hideContentPickerPanel()
  // If we close a content picker panel and the source menu is also closed, close the whole content picker.
  if (!xContentSourceMenu.value) {
    emit('close-content-picker')
  }
}

const activatePanel = (source: ContentPickerSource): void => {
  if (canAppUseNativeCameraAndFilePicker.value && source === ContentPickerSource.FileBrowser && props.appIntent) {
    postMessage(browserFile(props.appIntent))
  } else if (canAppUseNativeCameraAndFilePicker.value && source === ContentPickerSource.PhotoBooth && props.appIntent) {
    postMessage(openCamera(props.appIntent))
  } else if (
    source === ContentPickerSource.ScreenshareRecorder &&
    screenshareSupport.value &&
    !screenshareEnabledByDefault.value
  ) {
    if (chromeExtensionInstalled.value === true || isElectronApp) {
      pickContentSource({ source })
    } else {
      emit('show-extension-required')
    }
  } else if (isAppUsing('videoRecordingLimits') && source === ContentPickerSource.VideoRecorder) {
    pickContentSource({ source })
    return
  } else if (
    ((source === ContentPickerSource.FileBrowser ||
      source === ContentPickerSource.PhotoBooth ||
      source === ContentPickerSource.VideoRecorder) &&
      isMobile) ||
    source === ContentPickerSource.FileBrowser
  ) {
    // Do nothing because the hidden input will be activated based on
    // the ContentSourceMenuListItem properties.
    return
  }

  pickContentSource({ source })
}

// TODO: move this outside of ContentPicker so this component isn't coupled
// with posts/drafts
const createPoll = (pollData) => {
  const wishContent = {
    attachment_props: { ...pollData, type: 'poll' },
  }

  emit('update-active-draft', { wish_content: wishContent })
  closeSourceMenu()
}

// TODO: move this outside of ContentPicker so this component isn't coupled
// with posts/drafts
const saveAiTalkAudio = (data: {
  attachment: string
  attachment_caption: string
  wish_content: { attachment_props: { type: string; url: string; text: string } }
}) => {
  emit('update-active-draft', data)
  closeSourceMenu()
}

function checkAndCloseContentPicker(): void {
  if (contentPickerStore.isHiddenContentSourceMenu) {
    closeSourceMenu()
  }
}
onMounted((): void => {
  checkForPadletMiniChromeExtension().then((isInstalled) => {
    chromeExtensionInstalled.value = isInstalled
  })
  if (contentPickerStore.isFileBrowserTriggered) {
    nextTick(() => {
      document.getElementById('content-hidden-file-input')?.click()
      activatePanel(ContentPickerSource.FileBrowser)
    })
  }
})

onBeforeUnmount((): void => {
  if (uploadJob.value) {
    uploadJob.value.cancel()
  }
})

watch(
  () => contentPickerStore.isFileBrowserTriggered,
  (isFileBrowserTriggered) => {
    if (isFileBrowserTriggered) {
      document.getElementById('content-hidden-file-input')?.click()
      activatePanel(ContentPickerSource.FileBrowser)
    }
  },
)

expose({
  activatePanel,
})

return { __sfc: true,MAX_FILE_UPLOAD_SIZE, PhotoBoothV2, VideoRecorder, AudioRecorderV2, PadletBrowser, SurfacePadletPickerV2, DrawingCanvas, MapBrowser, LocationPickerV2, ScreenshareRecorder, SurfacePollBuilder, GoogleDrivePicker, AiTalk, props, emit, isChrome, isFirefox, isElectronApp, isMobile, contentPickerStore, activeContentPickerPanel, xContentSourceMenu, isHiddenContentSourceMenu, contentPickerContext, hideContentPickerPanel, hideContentSourceMenu, pickContentSource, isContentPickerContextMatch, chromeExtensionInstalled, uploadJob, activePanel, screenshareEnabledByDefault, screenshareSupport, canAppUseNativeCameraAndFilePicker, pollDraft, audioLengthLimit, videoLengthLimit, uploadFile, onUploadDone, onUploadCancel, onUploadReject, onUploadError, selectFile, selectLink, selectWall, startFileUpload, setCaption, closeSourceMenu, closePanel, activatePanel, createPoll, saveAiTalkAudio, checkAndCloseContentPicker, ContentPickerSource, isAppUsing, PadletSelectionIntent, ContentPickerSourceMenu }
}

})